import { IonIcon } from "@ionic/react";
import { addCircle, removeCircle, tabletLandscape } from "ionicons/icons";
import { IPicture } from "../../models";
import { NumberMoney } from "../../components/NumberMoney/NumberMoney";
import { usePictures } from "../Pictures/usePictures";
import { useCart } from "./useCart";

type Option = "none" | "frame" | "assembled";

const optionRowStyle = (selected: boolean, enabled: boolean) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 8px",
  marginBottom: 4,
  borderRadius: 8,
  border: selected
    ? "2px solid var(--ion-color-primary)"
    : "1px solid var(--ion-color-light-shade)",
  cursor: enabled ? "pointer" : "default",
  opacity: enabled ? 1 : 0.5,
});

const RadioDot = ({ selected }: { selected: boolean }) => (
  <div
    style={{
      width: 16,
      height: 16,
      flexShrink: 0,
      borderRadius: "50%",
      border: `2px solid ${
        selected ? "var(--ion-color-primary)" : "var(--ion-color-medium)"
      }`,
      backgroundColor: selected ? "var(--ion-color-primary)" : "transparent",
    }}
  />
);

const Stepper = ({
  value,
  canAdd,
  onChange,
}: {
  value: number;
  canAdd: boolean;
  onChange: (value: number) => void;
}) => (
  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
    <IonIcon
      icon={removeCircle}
      className={value > 0 ? "cursorP" : "op5"}
      color={value > 0 ? "primary" : "medium"}
      style={{ fontSize: 26 }}
      onClick={() => value > 0 && onChange(value - 1)}
    />
    <span className="b f15" style={{ minWidth: 16, textAlign: "center" }}>
      {value}
    </span>
    <IonIcon
      icon={addCircle}
      className={canAdd ? "cursorP" : "op5"}
      color={canAdd ? "primary" : "medium"}
      style={{ fontSize: 26 }}
      onClick={() => canAdd && onChange(value + 1)}
    />
  </div>
);

const FrameHeader = () => (
  <div
    className="f15 b"
    style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}
  >
    <IonIcon icon={tabletLandscape} />
    Blind ram
  </div>
);

/**
 * Frame choice for one picture in the cart: which units come without a frame, with a plain
 * (unassembled) frame, or stretched on an assembled frame.
 */
export const PictureFrameChoice = ({ pic }: { pic: IPicture }) => {
  const { cart, getPictureFrame, setPictureFrame, canHaveFrameAssembled } =
    useCart();
  const { equipmentPrice, equipmentState } = usePictures();

  const quantity = cart[pic.id] || 0;
  const { frames, assembled } = getPictureFrame(pic.id);
  const none = quantity - frames - assembled;

  // A choice already made stays visible even if the frame went out of stock meanwhile.
  const frameEnabled = !!equipmentState.frame || frames > 0;
  const showAssembled =
    canHaveFrameAssembled(pic) && (!!equipmentState.frameAssembled || assembled > 0);

  const label = (text: string, price?: number) => (
    <div style={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}>
      <div className="f15">{text}</div>
      {price !== undefined && (
        <div className="f13" style={{ color: "var(--ion-color-medium)" }}>
          + <NumberMoney value={price} />
        </div>
      )}
    </div>
  );

  if (quantity === 1) {
    const selected: Option =
      assembled > 0 ? "assembled" : frames > 0 ? "frame" : "none";
    const select = (option: Option) =>
      setPictureFrame(
        pic.id,
        option === "frame" ? 1 : 0,
        option === "assembled" ? 1 : 0
      );

    return (
      <div style={{ padding: "4px 2%" }}>
        <FrameHeader />
        <div
          style={optionRowStyle(selected === "none", true)}
          onClick={() => select("none")}
        >
          <RadioDot selected={selected === "none"} />
          {label("Bez rama")}
        </div>
        <div
          style={optionRowStyle(selected === "frame", frameEnabled)}
          onClick={() => frameEnabled && select("frame")}
        >
          <RadioDot selected={selected === "frame"} />
          {label("Rasklopljen ram", equipmentPrice.frame)}
        </div>
        {showAssembled && (
          <div
            style={optionRowStyle(selected === "assembled", true)}
            onClick={() => select("assembled")}
          >
            <RadioDot selected={selected === "assembled"} />
            {label("Sklopljen ram sa platnom", equipmentPrice.frameAssembled)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: "4px 2%" }}>
      <FrameHeader />
      <div style={optionRowStyle(false, frameEnabled)}>
        {label("Rasklopljen ram", equipmentPrice.frame)}
        <Stepper
          value={frames}
          canAdd={frameEnabled && none > 0}
          onChange={(value) => setPictureFrame(pic.id, value, assembled)}
        />
      </div>
      {showAssembled && (
        <div style={optionRowStyle(false, true)}>
          {label("Sklopljen ram sa platnom", equipmentPrice.frameAssembled)}
          <Stepper
            value={assembled}
            canAdd={none > 0}
            onChange={(value) => setPictureFrame(pic.id, frames, value)}
          />
        </div>
      )}
      <div className="f13" style={{ padding: "0 8px" }}>
        Bez rama: <span className="b">{none}</span>
      </div>
    </div>
  );
};
